import { useEffect, useMemo, useState } from "react";
import { AlertCircle, Plus, RotateCw, Trash2 } from "lucide-react";
import type { ColumnDef, SortingState } from "@tanstack/react-table";

import { useCurrentPageAccess } from "@/app/router";
import {
  useCreateMadupIndustryMappingMutation,
  useDeleteMadupIndustryMappingsMutation,
  useMadupIndustryMappingsQuery,
  useUpdateMadupIndustryMappingMutation,
  type MadupIndustryMappingRow,
} from "@/api/madupIndustryMapping";
import {
  useMonthlyPerformanceSlackChannelQuery,
  useUpdateMonthlyPerformanceSlackChannelMutation,
} from "@/api/monthlyPerformanceSlack";
import { Breadcrumbs } from "@/shared/components/layout/Breadcrumbs";
import { PageHeader } from "@/shared/components/layout/PageHeader";
import { DataTable } from "@/shared/components/table";
import { DataTableColumnHeader } from "@/shared/components/table/DataTableColumnHeader";
import { DataTableToolbar } from "@/shared/components/table/DataTableToolbar";
import { Alert, AlertDescription } from "@/shared/components/ui/alert";
import { Button } from "@/shared/components/ui/button";
import { Card } from "@/shared/components/ui/card";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";

// Slack channel IDs are uppercase alphanumeric prefixed with C / G / D
// (public, private/legacy group, DM). Slack v2 file upload only accepts IDs.
const SLACK_CHANNEL_ID_RE = /^[CGD][A-Z0-9]{8,}$/;

function SlackChannelSection() {
  const { canWriteCurrentPage } = useCurrentPageAccess();
  const { data, isLoading } = useMonthlyPerformanceSlackChannelQuery();
  const updateMutation = useUpdateMonthlyPerformanceSlackChannelMutation();

  const [draft, setDraft] = useState("");

  useEffect(() => {
    if (data?.channel != null) {
      setDraft(data.channel);
    }
  }, [data?.channel]);

  const trimmed = draft.trim();
  const isValidId = SLACK_CHANNEL_ID_RE.test(trimmed);
  const isDirty = trimmed !== (data?.channel ?? "");
  const canSubmit = canWriteCurrentPage && isDirty && isValidId && !updateMutation.isPending;
  const showFormatError = trimmed.length > 0 && !isValidId;

  const handleSave = () => {
    if (!canSubmit) return;
    updateMutation.mutate(trimmed);
  };

  return (
    <Card className="w-full max-w-[640px] space-y-4 p-4">
      <div>
        <h2 className="text-lg font-semibold">월별 결산 CSV 슬랙 채널</h2>
        <p className="text-sm text-muted-foreground">
          madflow가 매월 결산 CSV를 업로드한 뒤 알림을 보낼 슬랙 채널을 지정합니다.
        </p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="slack-channel">슬랙 채널 ID</Label>
        <Input
          id="slack-channel"
          placeholder="예: C0123ABCXYZ"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={!canWriteCurrentPage}
          aria-invalid={showFormatError}
        />
        <p className="text-xs text-muted-foreground">
          채널 <strong>이름</strong>이 아닌 <strong>채널 ID</strong>를 입력해야 합니다. Slack 데스크톱 앱에서
          채널 이름 우클릭 → <em>채널 세부정보 보기</em> → 하단 <em>채널 ID</em>를 복사해 사용하세요. madflow
          Slack 봇이 해당 채널에 초대되어 있어야 합니다.
        </p>
        {showFormatError && (
          <p className="text-xs text-destructive">
            올바른 채널 ID 형식이 아닙니다. C·G·D로 시작하는 대문자/숫자 9자 이상이어야 합니다.
          </p>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button onClick={handleSave} disabled={!canSubmit}>
          저장
        </Button>
        {data?.channel ? (
          <span className="text-sm text-muted-foreground">
            현재 설정: <span className="font-mono font-medium text-foreground">{data.channel}</span>
          </span>
        ) : (
          !isLoading && <span className="text-sm text-muted-foreground">아직 설정되지 않았습니다.</span>
        )}
      </div>
    </Card>
  );
}

type DialogMode = "create" | "update";

function IndustryMappingSection() {
  const { canWriteCurrentPage } = useCurrentPageAccess();
  const { data, isLoading, refetch } = useMadupIndustryMappingsQuery();

  const createMutation = useCreateMadupIndustryMappingMutation();
  const updateMutation = useUpdateMadupIndustryMappingMutation();
  const deleteMutation = useDeleteMadupIndustryMappingsMutation();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedRows, setSelectedRows] = useState<MadupIndustryMappingRow[]>([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<DialogMode>("create");
  const [editingRow, setEditingRow] = useState<MadupIndustryMappingRow | null>(null);
  const [formIndustry, setFormIndustry] = useState("");
  const [formMadupIndustry, setFormMadupIndustry] = useState("");
  const [formError, setFormError] = useState("");

  const filtered = useMemo(() => {
    const rows = data?.mappings ?? [];
    if (!searchKeyword) return rows;
    const keyword = searchKeyword.toLowerCase();
    return rows.filter(
      (m) =>
        m.industry.toLowerCase().includes(keyword) ||
        m.madup_industry.toLowerCase().includes(keyword)
    );
  }, [data?.mappings, searchKeyword]);

  const sorted = useMemo(() => {
    if (sorting.length === 0) return filtered;
    const sort = sorting[0];
    const next = [...filtered];
    next.sort((a, b) => {
      const aValue = a[sort.id as keyof MadupIndustryMappingRow];
      const bValue = b[sort.id as keyof MadupIndustryMappingRow];
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (aValue < bValue) return sort.desc ? 1 : -1;
      if (aValue > bValue) return sort.desc ? -1 : 1;
      return 0;
    });
    return next;
  }, [filtered, sorting]);

  const paginated = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, page, pageSize]);

  const columns = useMemo<ColumnDef<MadupIndustryMappingRow>[]>(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllPageRowsSelected()}
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
            onClick={(e) => e.stopPropagation()}
          />
        ),
        size: 50,
        minSize: 50,
        maxSize: 50,
        enableSorting: false,
        enableHiding: false,
        enableResizing: false,
      },
      {
        accessorKey: "industry",
        header: ({ column }) => <DataTableColumnHeader column={column} title="industry (ENUM)" />,
        cell: ({ row }) => <span className="font-mono text-sm">{row.original.industry}</span>,
      },
      {
        accessorKey: "madup_industry",
        header: ({ column }) => <DataTableColumnHeader column={column} title="madup_industry" />,
      },
      {
        accessorKey: "updated_at",
        header: ({ column }) => <DataTableColumnHeader column={column} title="수정일시" />,
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {new Date(row.original.updated_at).toLocaleString("ko-KR")}
          </span>
        ),
      },
    ],
    []
  );

  const handleOpenCreate = () => {
    setDialogMode("create");
    setEditingRow(null);
    setFormIndustry("");
    setFormMadupIndustry("");
    setFormError("");
    setDialogOpen(true);
  };

  const handleOpenUpdate = (row: MadupIndustryMappingRow) => {
    setDialogMode("update");
    setEditingRow(row);
    setFormIndustry(row.industry);
    setFormMadupIndustry(row.madup_industry);
    setFormError("");
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");

    const industry = formIndustry.trim();
    const madupIndustry = formMadupIndustry.trim();
    if (!industry || !madupIndustry) {
      setFormError("모든 필드를 입력해주세요.");
      return;
    }

    try {
      if (dialogMode === "create") {
        await createMutation.mutateAsync({ industry, madup_industry: madupIndustry });
      } else if (editingRow) {
        await updateMutation.mutateAsync({
          industry: editingRow.industry,
          request: { madup_industry: madupIndustry },
        });
      }
      handleCloseDialog();
    } catch (error) {
      setFormError(error instanceof Error ? error.message : "작업에 실패했습니다.");
    }
  };

  const handleDelete = async () => {
    if (selectedRows.length === 0) return;
    if (!confirm(`선택한 ${selectedRows.length}개 매핑을 삭제하시겠습니까?`)) return;

    await deleteMutation.mutateAsync(selectedRows.map((r) => r.industry));
    setSelectedRows([]);
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Industry 매핑"
        description="industry_category ENUM을 madup_industry(한글 표기)로 변환하는 매핑을 관리합니다. 행을 클릭하면 수정할 수 있습니다."
        actions={
          <>
            <Button variant="outline" size="icon" onClick={() => refetch()}>
              <RotateCw className="h-4 w-4" />
            </Button>
            <Button onClick={handleOpenCreate} disabled={!canWriteCurrentPage}>
              <Plus className="mr-2 h-4 w-4" />
              추가
            </Button>
            <Button
              variant="destructive"
              disabled={!canWriteCurrentPage || selectedRows.length === 0 || deleteMutation.isPending}
              onClick={handleDelete}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              삭제 ({selectedRows.length})
            </Button>
          </>
        }
      />

      <DataTable
        columns={columns}
        data={paginated}
        totalCount={sorted.length}
        page={page}
        pageSize={pageSize}
        sorting={sorting}
        onSortingChange={setSorting}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        isLoading={isLoading}
        enableRowSelection
        getRowId={(row) => row.industry}
        onRowSelectionChange={setSelectedRows}
        onRowClick={canWriteCurrentPage ? handleOpenUpdate : undefined}
        toolbar={
          <DataTableToolbar
            searchValue={searchKeyword}
            onSearchChange={(v) => {
              setSearchKeyword(v);
              setPage(1);
            }}
            showReset={!!searchKeyword}
            onReset={() => {
              setSearchKeyword("");
              setPage(1);
            }}
            searchPlaceholder="industry / madup_industry 검색"
          />
        }
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>{dialogMode === "create" ? "매핑 추가" : "매핑 수정"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="form-industry">industry (ENUM)</Label>
              <Input
                id="form-industry"
                value={formIndustry}
                onChange={(e) => setFormIndustry(e.target.value)}
                placeholder="예: FASHION"
                maxLength={64}
                disabled={dialogMode === "update"}
                required
              />
              {dialogMode === "update" && (
                <p className="text-xs text-muted-foreground">
                  industry는 기본 키이므로 수정할 수 없습니다. 변경하려면 삭제 후 다시 추가해주세요.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="form-madup-industry">madup_industry</Label>
              <Input
                id="form-madup-industry"
                value={formMadupIndustry}
                onChange={(e) => setFormMadupIndustry(e.target.value)}
                placeholder="예: 패션"
                maxLength={64}
                required
              />
            </div>

            {formError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                취소
              </Button>
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                {dialogMode === "create" ? "추가" : "수정"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function InfoTrendDataManagementPage() {
  return (
    <div className="space-y-8">
      <Breadcrumbs />
      <PageHeader
        title="Trend 데이터 관리"
        description="월별 결산 CSV 알림 채널과 industry_category ↔ madup_industry 매핑을 관리합니다."
      />

      <SlackChannelSection />
      <IndustryMappingSection />
    </div>
  );
}
